import {
  AddressModel,
  UnitBasicInfoModel,
  UnitListingDetailsModel,
  SpaceListingModel,
  MediaModel,
  FeatureListingModel,
} from 'src/api/models';
import { DATE_FORMATS, formatUtcDateTimeToTimeZone } from '@belong/common';
import { HOUSE_PAGE_STRINGS } from './house-page.strings';

// Mirrors belong-platform's MilestoneName metadata order (Belong.Models.Core.Timeline.MilestoneName).
// Used to gate tour availability messaging independently of isComingSoon, which alone can't tell
// "pre-ShowReady" apart from "ShowReady but before Full Listing Published" (LOVE-335).
// `satisfies` rather than an annotation: a `Record<string, number>` discards the
// literal keys, and every lookup below then comes back `number | undefined`.
const MILESTONE_ORDER = {
  None: 0,
  AgreementSigned: 10,
  InspectionScheduled: 20,
  InspectionCompleted: 30,
  PostInspectionProposalSent: 40,
  PostInspectionProposalApproved: 50,
  ShowReady: 60,
  FullListingPublished: 70,
  ListingRepublished: 70,
  LeaseExecuted: 80,
  ResidentPlaced: 81,
  MoveInPrepped: 90,
  MoveInReady: 100,
  ResidentMovedIn: 110,
  ManagingAdoptedResidents: 120,
  ResidentGaveMoveOutNotice: 130,
  TimelineReset: 999,
} satisfies Record<UnitBasicInfoModel['currentMilestone'], number>;

export function isMilestoneAtOrAfter(
  currentMilestone: UnitBasicInfoModel['currentMilestone'] | undefined,
  target: keyof typeof MILESTONE_ORDER,
): boolean {
  if (!currentMilestone) return false;
  return MILESTONE_ORDER[currentMilestone] >= MILESTONE_ORDER[target];
}

export function isSelfTourOffered(
  listingInfo?: UnitListingDetailsModel['listingInfo'],
): boolean {
  return (
    !!listingInfo?.isSelfServeVisitsAllowed &&
    !!listingInfo.homeownerAgreedToSelfTours
  );
}

export function getSelfTourDayLabel(
  startOn: string,
  now: number,
  timeZone?: string,
) {
  const toDayKey = (dateTime: string | number) =>
    formatUtcDateTimeToTimeZone({
      dateTime,
      format: DATE_FORMATS.STRIPPED_ISO,
      timeZone,
    });
  const day = toDayKey(startOn);
  const today = toDayKey(now);
  const tomorrow = new Date(`${today}T00:00:00Z`);
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);

  if (day === today) return HOUSE_PAGE_STRINGS['tour.selfTourToday'];
  if (day === tomorrow.toISOString().slice(0, 10)) {
    return HOUSE_PAGE_STRINGS['tour.selfTourTomorrow'];
  }

  return formatUtcDateTimeToTimeZone({
    dateTime: startOn,
    format: DATE_FORMATS.MONTH_DAY_SHORT,
    timeZone,
  });
}

// EstimatedShowReadyOn is an ops-entered estimate that can go stale (LOVE-336) — it never gates
// tourability itself, but showing a "ready by {date}" message once that date is today or in the
// past would be actively misleading, so callers should fall back to date-less copy in that case.
export function isEstimatedShowReadyOnInFuture(
  estimatedShowReadyOn?: string,
): boolean {
  if (!estimatedShowReadyOn) return false;
  const todayStartOfDay = new Date(new Date().toDateString());
  return new Date(`${estimatedShowReadyOn}T12:00:00`) > todayStartOfDay;
}

// Converts kebab-case values from the new inspection to Title Case for display.
// Old inspection values (already Title Case) pass through unchanged.
// e.g. "forced-air" → "Forced Air", "Gas" → "Gas"
export function toDisplayValue(value?: string): string | undefined {
  return value
    ?.split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Maps numberOfSpots enum strings from the new inspection's generic "Parking" feature
const numberOfSpotsEnum: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  'four-or-more': 4,
};

export function parseParkingSpots(attributes: Record<string, unknown>): number {
  const raw = (attributes.numberOfSpots || attributes.NumberOfSpots) as string;
  return numberOfSpotsEnum[raw] || parseInt(raw) || 0;
}

const applianceCountEnum: Record<string, number> = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  'three-or-more': 3,
};

function parseApplianceCount(raw: unknown): number | undefined {
  const value = String(raw).trim().toLowerCase();
  if (value in applianceCountEnum) return applianceCountEnum[value];
  const parsed = parseInt(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function hasZeroWashers(feature: FeatureListingModel): boolean {
  const { attributes } = feature;
  const washers = attributes?.numberOfWashers ?? attributes?.NumberOfWashers;
  return parseApplianceCount(washers) === 0;
}

export function hasNoLaundryAppliances(feature: FeatureListingModel): boolean {
  const { attributes } = feature;
  const dryers = attributes?.numberOfDryers ?? attributes?.NumberOfDryers;
  return hasZeroWashers(feature) && !((parseApplianceCount(dryers) ?? 0) > 0);
}

// TODO: Global search formatAddress and standarize?
export function formatAddress(
  address: AddressModel,
  unitNumber?: string,
): string {
  const showNeighborhood = address.displayNeighborhood && address.neighborhood;
  const cityOrNeighborhood = showNeighborhood
    ? address.neighborhood
    : address.city;

  if (!address.streetAddress) {
    return `${cityOrNeighborhood}, ${address.state}`;
  }

  return `${address.streetAddress}${unitNumber ? ` Unit ${unitNumber}` : ''},
    ${`${cityOrNeighborhood}, ${address.state} ${address.zipcode}`.replace(' ', '\u00a0')}`;
}

export type Section = SpaceListingModel & {
  media: MediaModel[];
};

export function getMediaSectionNames(home: UnitListingDetailsModel) {
  const EXTERIOR_COMMON_ID = 'exterior-combined';
  const homeSpaces = home.spaces || [];

  const spaces: SpaceListingModel[] = [];
  const exteriorSpaces: SpaceListingModel[] = [];

  homeSpaces.forEach((space) => {
    space.type === 'Exterior' ? exteriorSpaces.push(space) : spaces.push(space);
  });

  const exteriorIds = exteriorSpaces.map((space) => space.id);

  // Get the exterior space that will be used to represent all exterior spaces and add it to the list of spaces
  const commonExteriorSpace = exteriorSpaces.sort(
    (a, b) => a.sortOrder - b.sortOrder,
  )[0]; // Sort them to keep the one with minimum sortOrder

  const sectionIdToMedia: Record<string, MediaModel[]> = {};
  let numberOfPhotos = 0;

  home.tourMedia!.forEach((media) => {
    if (media.ownerId && media.ownerType === 'Space') {
      numberOfPhotos = numberOfPhotos + 1;
      const sectionId = exteriorIds?.includes(media.ownerId)
        ? EXTERIOR_COMMON_ID
        : media.ownerId;

      if (sectionIdToMedia[sectionId]) {
        sectionIdToMedia[sectionId].push(media);
      } else {
        // `!`: `sortOrder` is optional on `MediaModel` only. Asserting keeps the
        // existing comparator exactly — coalescing to 0 instead would move
        // media with no sortOrder to the front, where today they stay put.
        sectionIdToMedia[sectionId] = [media].sort(
          (a, b) => a.sortOrder! - b.sortOrder!,
        );
      }
    }
  });

  // Add media to the sections (rooms and spaces)
  const sections = spaces
    .filter((section) => sectionIdToMedia[section.id])
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const mediaSections = sections.map((section) => ({
    ...section,
    // `!`: `sections` is the list already filtered on this same lookup above.
    media: sectionIdToMedia[section.id]!,
  }));

  const mediaSectionNames = mediaSections.sort(
    (a, b) => a.sortOrder - b.sortOrder,
  );

  // Exterior spaces always go first
  if (commonExteriorSpace && sectionIdToMedia[EXTERIOR_COMMON_ID]) {
    mediaSections.unshift({
      ...commonExteriorSpace,
      id: EXTERIOR_COMMON_ID,
      media: sectionIdToMedia[EXTERIOR_COMMON_ID],
    });
  }

  return { mediaSectionNames, numberOfPhotos, sectionsMedia: sectionIdToMedia };
}

export function getFeaturesDetails(features: FeatureListingModel[]) {
  const details: any = {};
  details.hasAirCondition = features.find((feature) => feature.name === 'A/C');
  details.parkingSpots = 0;
  details.parkingType = 'No Parking';
  details.isCentralAirCondition =
    details.hasAirCondition &&
    (details.hasAirCondition.attributes?.IsCentralAirCondition ||
      details.hasAirCondition.attributes?.IsCentralSystem === 'yes');
  const heatAttrs = features.find(
    (feature) => feature.name === 'Heat',
  )?.attributes;
  details.heaterType =
    toDisplayValue(heatAttrs?.HeatType) ||
    toDisplayValue(heatAttrs?.HeatSource) ||
    toDisplayValue(heatAttrs?.HeatDistribution);
  details.interiorHighlights = {};
  details.exteriorHighlights = {};

  const { interiorHighlights, exteriorHighlights } = details;

  features.forEach((feature) => {
    // `!`: `category` is optional on the model, and a feature without one has
    // always been bucketed under the literal key "undefined" here. Asserting
    // keeps that; skipping those features instead would drop them from the page.
    const category = feature.category!;

    if (feature.isCommunalFeature) {
      exteriorHighlights[category] = exteriorHighlights[category] || [];
      exteriorHighlights[category].push(feature);
    } else {
      interiorHighlights[category] = interiorHighlights[category] || [];
      interiorHighlights[category].push(feature);
    }
  });

  return details;
}

const LABEL_COLOR_TYPES = {
  TRANSPARENT: 'bg-transparent',
  BLUE: 'bg-blue',
  NAVY: 'bg-navy',
  GREEN: 'bg-green',
  DARK_GREEN: 'bg-dark-green',
  RED: 'bg-red',
  GRAY: 'bg-gray',
  DARK_GRAY: 'bg-dark-gray',
  YELLOW: 'bg-yellow',
  WHITE: 'bg-white',
  GRAY_BORDER: 'bg-gray-border',
};

export function getTagColor(level?: string) {
  switch (level) {
    case 'Good':
      return LABEL_COLOR_TYPES.DARK_GREEN;
    case 'Average':
      return LABEL_COLOR_TYPES.YELLOW;
    case 'Bad':
      return LABEL_COLOR_TYPES.DARK_GRAY;
    default:
      return LABEL_COLOR_TYPES.BLUE;
  }
}

export function getTag(level?: string) {
  switch (level) {
    case 'Bad':
      return 'Not Recommended';
    case 'Good':
      return 'Recommended';
    default:
      return LABEL_COLOR_TYPES.BLUE;
  }
}

export function getSection(section?: string) {
  try {
    // `!`, not `?.`: a missing section throws here, and the `catch` below is
    // what turns that into the empty result. Optional chaining would move the
    // throw to `.length` on the next line, out of this expression.
    const splitSectionNameArray = section!.split('-');
    if (splitSectionNameArray.length > 1) {
      // Id is always last element
      const sectionId = splitSectionNameArray.pop();

      // We have sections that have '-' in their names, like In-Law unit, so we have to join again after popping the id.
      const sectionName = splitSectionNameArray.join('-');
      return [sectionName, sectionId];
    }
    return splitSectionNameArray;
  } catch {
    return [];
  }
}
